import React, { useEffect, useRef, useState } from "react";
import "./styles.css";
import logoAirlines from "../Login/logoAirlines.png"
import useAppData from "@/store/useAppData";
import useRouter from "@/store/useRouter";
import { RouterStoreItemKey } from "@/store/useRouter/types";
import useCardLogic from "./useCardLogic";
import useDeviceType from "@/Utils/useDeviceType";

export interface CardProps {
    title: string;
    children: React.ReactNode;
    onSettingsClick?: () => void;
    onProfileClick?: () => void;
    leftSlot?: React.ReactNode;
    suppressInit?: boolean;
    displaySelectedStock?: boolean;
    displayLogo?: boolean;
    displayProfile?: boolean;
}

const Card: React.FC<CardProps> = ({
    title,
    children,
    onSettingsClick,
    onProfileClick,
    leftSlot,
    suppressInit = false,
    displaySelectedStock = true,
    displayLogo = true,
    displayProfile = false
}) => {

    const logic = useCardLogic({ title, suppressInit });

    const go = useRouter(state => state.go);
    const loggedUser = useAppData(state => state.user);
    const setLoggedUser = useAppData(state => state.setUser);
    const selectedStock = useAppData(state => state.selectedStock);
    const selectedModule = useAppData(state => state.selectedModule);

    const [profileOpen, setProfileOpen] = useState(false);
    const profileRef = useRef<HTMLDivElement>(null);

    const deviceType = useDeviceType();

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                profileRef.current &&
                !profileRef.current.contains(e.target as Node)
            ) {
                setProfileOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="card">
            <div className="card-header">
                <div className="card-header-left">
                    {displayLogo && <img src={logoAirlines} className="login-logo-img1" />}
                    {leftSlot /* keep empty if you don't need it */}
                </div>

                <div className="card-header-title">{title}</div>

                <div className="card-header-right">
                    {(onSettingsClick || title === "Nastavení") && <div className="stock-display">Modul: <select
                        value={selectedModule?.id ?? undefined}
                        onChange={logic.onChangeSelectedModule}
                    >
                        {logic.modulesState.map((o) => (
                            <option key={o.id} value={o.id}>
                                {o.name}
                            </option>
                        ))}
                    </select></div>}
                    {!onSettingsClick && title != "Nastavení" && displaySelectedStock && <div className="stock-display">{`Sklad: ${selectedStock.name}`}</div>}
                    {loggedUser?.userrole === "admin" && onSettingsClick && deviceType === "desktop" && <button
                        type="button"
                        className="card-icon-button"
                        onClick={onSettingsClick}
                        aria-label="Nastavení"
                        title="Nastavení"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
                                stroke="currentColor"
                                strokeWidth="2"
                            />
                            <path
                                d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21a2 2 0 1 1-4 0v-.08a1.7 1.7 0 0 0-.4-1.1 1.7 1.7 0 0 0-1-.6 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H2a2 2 0 1 1 0-4h.08a1.7 1.7 0 0 0 1.1-.4 1.7 1.7 0 0 0 .6-1 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6c.39 0 .77-.14 1.06-.4.29-.27.47-.64.5-1.04V3a2 2 0 1 1 4 0v.08c.03.4.21.77.5 1.04.29.26.67.4 1.06.4a1.7 1.7 0 0 0 1-.6l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c0 .39.14.77.4 1.06.27.29.64.47 1.04.5H21a2 2 0 1 1 0 4h-.08c-.4.03-.77.21-1.04.5-.26.29-.4.67-.4 1.06Z"
                                stroke="currentColor"
                                strokeWidth="2"
                            />
                        </svg>
                    </button>}

                    {((onSettingsClick && deviceType === "desktop") || displayProfile ) && <div ref={profileRef} className="profile-wrapper">
                        <button
                            type="button"
                            className="card-icon-button"
                            // onClick={onProfileClick}
                            onClick={() => setProfileOpen(v => !v)}
                            aria-label="Profil"
                            title="Profil"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M12 12a4.5 4.5 0 1 0-4.5-4.5A4.5 4.5 0 0 0 12 12Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                />
                                <path
                                    d="M4 20a8 8 0 0 1 16 0"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </button>
                        {profileOpen && (
                            <div className="profile-dropdown">
                                <div className="profile-item">
                                    👤 {loggedUser?.name}
                                </div>

                                <div className="profile-divider" />

                                <div
                                    className="profile-item logout"
                                    onClick={() => {
                                        setLoggedUser(null);
                                        go({ key: RouterStoreItemKey.LOGIN });
                                    }}
                                >
                                    Odhlásit
                                </div>
                            </div>
                        )}
                    </div>}

                </div>
            </div>

            <div className="card-body">
                <div className="card-body-content">
                    {children}
                </div>
            </div>
        </div>
    );
};

export default Card;